/**
 * OTAジョブ詳細・進捗監視ページ
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { otaApi, shadowApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Alert, Modal } from '../components/ui';

const JOB_STATUS_LABELS = {
  IN_PROGRESS: { label: '実行中', color: 'bg-blue-100 text-blue-700', icon: 'refresh' },
  QUEUED: { label: '待機中', color: 'bg-yellow-100 text-yellow-700', icon: 'clock' },
  COMPLETED: { label: '完了', color: 'bg-green-100 text-green-700', icon: 'check' },
  CANCELED: { label: 'キャンセル', color: 'bg-slate-100 text-slate-600', icon: 'x' },
  DELETION_IN_PROGRESS: { label: '削除中', color: 'bg-red-100 text-red-700', icon: 'trash' },
};

const EXEC_STATUS_LABELS = {
  QUEUED: { label: '待機中', color: 'bg-yellow-100 text-yellow-700' },
  IN_PROGRESS: { label: '実行中', color: 'bg-blue-100 text-blue-700' },
  SUCCEEDED: { label: '成功', color: 'bg-green-100 text-green-700' },
  FAILED: { label: '失敗', color: 'bg-red-100 text-red-700' },
  TIMED_OUT: { label: 'タイムアウト', color: 'bg-orange-100 text-orange-700' },
  REJECTED: { label: '拒否', color: 'bg-red-100 text-red-700' },
  REMOVED: { label: '削除済', color: 'bg-slate-100 text-slate-600' },
  CANCELED: { label: 'キャンセル', color: 'bg-slate-100 text-slate-600' },
};

export default function OTAJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // キャンセルモーダル
  const [showCancel, setShowCancel] = useState(false);
  const [cancelComment, setCancelComment] = useState('');
  const [canceling, setCanceling] = useState(false);

  // ロールバック検出（agent自己更新のみ: Shadowのreportedバージョンとジョブのバージョンを照合）
  const [versionMismatches, setVersionMismatches] = useState([]);

  // データ取得
  const fetchData = useCallback(async () => {
    try {
      const [jobData, execData] = await Promise.all([
        otaApi.get(jobId),
        otaApi.getExecutions(jobId),
      ]);
      setJob(jobData);
      setExecutions(execData.executions || []);
      setSummary(execData.summary || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 自動更新
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchData();
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // 実行中は自動更新をON
  useEffect(() => {
    if (job?.status === 'IN_PROGRESS' && !autoRefresh) {
      setAutoRefresh(true);
    } else if (job?.status !== 'IN_PROGRESS' && autoRefresh) {
      setAutoRefresh(false);
    }
  }, [job?.status, autoRefresh]);

  // ジョブ完了後のバージョン照合（agent自己更新のみ）
  // rauc-update はエージェント報告バージョン(=agentのバージョン)とバンドルバージョンが
  // 一致しないため照合対象外（下の注意書きで案内）
  useEffect(() => {
    if (job?.status !== 'COMPLETED' || job?.operation === 'rauc-update') {
      setVersionMismatches([]);
      return;
    }
    const succeeded = executions.filter((e) => e.status === 'SUCCEEDED').slice(0, 20);
    if (succeeded.length === 0) return;

    let cancelled = false;
    (async () => {
      const mismatches = [];
      for (const exec of succeeded) {
        const thingName = exec.thingArn?.split('/').pop();
        if (!thingName) continue;
        try {
          const shadowData = await shadowApi.get(thingName);
          const reportedVersion = shadowData?.state?.reported?.version;
          if (reportedVersion && reportedVersion !== job.firmwareVersion) {
            mismatches.push({ thingName, reportedVersion });
          }
        } catch (err) {
          // Shadow未取得は照合スキップ
        }
      }
      if (!cancelled) setVersionMismatches(mismatches);
    })();
    return () => { cancelled = true; };
  }, [job?.status, job?.operation, job?.firmwareVersion, executions]);

  // キャンセル
  const handleCancel = async () => {
    setCanceling(true);
    try {
      await otaApi.cancel(jobId, cancelComment);
      setShowCancel(false);
      setCancelComment('');
      await fetchData();
    } catch (err) {
      alert('キャンセルエラー: ' + err.message);
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '-';
    return new Date(isoStr).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const extractThingName = (thingArn) => {
    if (!thingArn) return '-';
    return thingArn.split('/').pop();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="error">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/ota')}>
          <Icon name="chevronLeft" className="w-4 h-4" />
          一覧に戻る
        </Button>
      </div>
    );
  }

  const statusInfo = JOB_STATUS_LABELS[job?.status] || {
    label: job?.status,
    color: 'bg-slate-100',
  };

  const progressPercent = summary?.total > 0
    ? Math.round(((summary.succeeded + summary.failed + summary.canceled) / summary.total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/ota')}>
          <Icon name="chevronLeft" className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-slate-900">{jobId}</h2>
          <p className="text-sm text-slate-500">{job?.description || 'OTAジョブ詳細'}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded text-primary-500 focus:ring-primary-500"
            />
            自動更新
          </label>
          <Button variant="secondary" onClick={fetchData}>
            <Icon name="refresh" className="w-4 h-4" />
            更新
          </Button>
          {job?.status === 'IN_PROGRESS' && (
            <Button variant="danger" onClick={() => setShowCancel(true)}>
              キャンセル
            </Button>
          )}
        </div>
      </div>

      {/* RAUC更新: 完了後の確認案内 */}
      {job?.operation === 'rauc-update' && job?.status === 'COMPLETED' && (
        <Alert variant="warning">
          <p className="font-medium">RAUCバンドル更新: 最終確認が必要です</p>
          <p className="text-sm mt-1">
            ジョブの「成功」は rauc install 完了時点の報告です。デバイスはこの後再起動し、
            初回起動時に PX4 ファームウェア書き込み（約4分）が実行されます。
            書き込みに失敗した場合は旧バージョンへ自動ロールバックされます（クラウドからは失敗が見えません）。
            デバイス再接続後に、デバイス詳細画面で FW バージョンが更新されていることを確認してください。
          </p>
        </Alert>
      )}

      {/* agent自己更新: バージョン不一致（ロールバックの可能性）警告 */}
      {versionMismatches.length > 0 && (
        <Alert variant="warning">
          <p className="font-medium">バージョン不一致を検出しました（ロールバックまたは未反映の可能性）</p>
          <ul className="text-sm mt-1 space-y-1">
            {versionMismatches.map((m) => (
              <li key={m.thingName} className="font-mono">
                {m.thingName}: 報告バージョン {m.reportedVersion}（期待: {job?.firmwareVersion}）
              </li>
            ))}
          </ul>
          <p className="text-xs mt-2">
            デバイスが再接続直後の場合は報告が未反映のこともあります。時間をおいて再確認してください。
          </p>
        </Alert>
      )}

      {/* ステータスカード */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* 基本情報 */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusInfo.color}`}>
              <Icon name={statusInfo.icon || 'info'} className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">ステータス</p>
              <p className={`text-lg font-semibold ${statusInfo.color.replace('bg-', 'text-').replace('-100', '-700')}`}>
                {statusInfo.label}
              </p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">FWバージョン:</span>
              <span className="text-slate-700 font-medium">v{job?.firmwareVersion}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">更新方式:</span>
              <span className="text-slate-700">
                {job?.operation === 'rauc-update' ? 'RAUCバンドル (OS)' : 'Agent更新'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">タイプ:</span>
              <span className="text-slate-700">{job?.targetSelection}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">作成日時:</span>
              <span className="text-slate-700">{formatDate(job?.createdAt)}</span>
            </div>
            {job?.cancelledAt && (
              <div className="flex justify-between">
                <span className="text-slate-500">キャンセル:</span>
                <span className="text-slate-700">{formatDate(job?.cancelledAt)}</span>
              </div>
            )}
          </div>
        </Card>

        {/* 進捗 */}
        <Card>
          <h3 className="text-sm font-medium text-slate-500 mb-4">進捗状況</h3>
          {summary ? (
            <>
              <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div
                  className="absolute left-0 top-0 h-full bg-green-500 transition-all"
                  style={{ width: `${(summary.succeeded / summary.total) * 100}%` }}
                />
                <div
                  className="absolute top-0 h-full bg-red-500 transition-all"
                  style={{
                    left: `${(summary.succeeded / summary.total) * 100}%`,
                    width: `${(summary.failed / summary.total) * 100}%`,
                  }}
                />
                <div
                  className="absolute top-0 h-full bg-blue-500 transition-all"
                  style={{
                    left: `${((summary.succeeded + summary.failed) / summary.total) * 100}%`,
                    width: `${(summary.inProgress / summary.total) * 100}%`,
                  }}
                />
              </div>
              <p className="text-2xl font-bold text-slate-900 mb-2">{progressPercent}%</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span>成功: {summary.succeeded}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span>失敗: {summary.failed}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span>実行中: {summary.inProgress}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span>待機: {summary.queued}</span>
                </div>
              </div>
            </>
          ) : (
            <p className="text-slate-500 text-center py-4">データなし</p>
          )}
        </Card>

        {/* サマリー */}
        <Card>
          <h3 className="text-sm font-medium text-slate-500 mb-4">対象デバイス</h3>
          <p className="text-3xl font-bold text-slate-900 mb-2">{summary?.total || 0}</p>
          <p className="text-sm text-slate-500">台のデバイス</p>
          <div className="mt-4 text-xs text-slate-500">
            <p>対象:</p>
            <ul className="mt-1 space-y-1">
              {job?.targets?.slice(0, 3).map((t, i) => (
                <li key={i} className="truncate font-mono">{extractThingName(t)}</li>
              ))}
              {job?.targets?.length > 3 && (
                <li className="text-primary-500">他 {job.targets.length - 3} 件...</li>
              )}
            </ul>
          </div>
        </Card>
      </div>

      {/* 実行状況一覧 */}
      <Card title="デバイス別実行状況" padding="p-0">
        {executions.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            実行データなし
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">デバイス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">ステータス</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">キュー日時</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">開始日時</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">更新日時</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {executions.map((exec, idx) => {
                  const execStatus = EXEC_STATUS_LABELS[exec.status] || {
                    label: exec.status,
                    color: 'bg-slate-100',
                  };
                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon name="drone" className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-900">
                            {extractThingName(exec.thingArn)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${execStatus.color}`}>
                          {execStatus.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatDate(exec.queuedAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatDate(exec.startedAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {formatDate(exec.lastUpdatedAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* キャンセルモーダル */}
      <Modal
        isOpen={showCancel}
        onClose={() => setShowCancel(false)}
        title="ジョブキャンセル"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCancel(false)}>
              戻る
            </Button>
            <Button variant="danger" onClick={handleCancel} loading={canceling}>
              キャンセル実行
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            OTAジョブをキャンセルします。実行中のデバイスは途中で停止されます。
          </p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              キャンセル理由（任意）
            </label>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
              placeholder="例: 緊急停止、問題発見..."
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
